import { defineStore } from "pinia";
import { reactive, ref } from "vue";

import {
  getServiceListWithResponseByCode,
  runChainServiceByIndividual,
} from "@/entities/template";
import {
  fetcherEvents,
  showServiceNotification,
} from "@/shared/lib/centrifuge";
import { generateRequestUuid } from "@/shared/lib/uuid";
import { ServiceQueueStepStatus } from "@/shared/config/serviceQueueStepStatuses";
import { ServiceExecutionStatus } from "@/shared/config/serviceExecutionStatuses";

const UI_ONLY_STEP_CODES = new Set([
  "manual_input",
  "show-check-info",
  "internal_databases",
  "manual",
]);

const ADVANCE_AFTER_MS = 60_000;
const FINAL_TIMEOUT_MS = 960_000;
// Абсолютный потолок ожидания шага: повторные NOTIFICATION (КДП) перезапускают
// FINAL_TIMEOUT_MS, но суммарно шаг не ждёт дольше этого значения.
const MAX_STEP_TOTAL_MS = 1_800_000;
const MIN_STEP_DURATION_MS = 1000;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function createStepState(step) {
  return {
    uuid: step.uuid,
    code: step.code,
    name: step.name,
    status: ServiceQueueStepStatus.IDLE,
    data: null,
    error: null,
  };
}

function buildPayload(schema, identifier, options = {}) {
  const props = schema?.request?.properties || {};
  const payload = {};

  for (const [key, prop] of Object.entries(props)) {
    payload[key] = prop.default ?? (prop.type === "boolean" ? false : "");
  }

  const personIdentifier = options.personIdentifier ?? identifier ?? "";
  const businessIdentifier = options.businessIdentifier ?? identifier ?? "";

  if ("person_identifier" in props)
    payload.person_identifier = personIdentifier || "";
  if ("business_identifier" in props)
    payload.business_identifier = businessIdentifier || "";

  payload.identifier = identifier || personIdentifier || "";

  return payload;
}

export const useServiceQueueStore = defineStore("serviceQueue", () => {
  const queues = reactive(new Map());
  const pendingByRequestUuid = new Map();
  const wsUnsubscribe = ref(null);
  const version = ref(0);

  function bumpVersion() {
    version.value++;
  }

  function ensureWsSubscription() {
    if (wsUnsubscribe.value) return;
    wsUnsubscribe.value = fetcherEvents.on(handleWsMessage);
  }

  function teardownWsSubscriptionIfIdle() {
    if (pendingByRequestUuid.size === 0 && wsUnsubscribe.value) {
      wsUnsubscribe.value();
      wsUnsubscribe.value = null;
    }
  }

  function handleWsMessage(data) {
    if (data?.type !== "FETCHER") {
      return;
    }

    const requestUuid = data.request_uuid;
    if (!requestUuid) {
      return;
    }

    const pending = pendingByRequestUuid.get(requestUuid);
    if (!pending) {
      return;
    }

    const wsPayload = data.payload;
    if (!wsPayload) {
      return;
    }
    if (wsPayload.service_code !== pending.stepCode) {
      return;
    }

    if (wsPayload.status === ServiceExecutionStatus.PENDING) {
      return;
    }

    // NOTIFICATION — ожидание подтверждения клиента (КДП). Не завершаем шаг,
    // перезапускаем таймаут ожидания, т.к. клиент может подтверждать дольше.
    if (wsPayload.status === ServiceExecutionStatus.NOTIFICATION) {
      showServiceNotification(wsPayload);
      pending.rescheduleFinalTimeout?.();
      return;
    }

    clearTimeout(pending.advanceTimeoutId);
    clearTimeout(pending.finalTimeoutId);
    pendingByRequestUuid.delete(requestUuid);

    const queue = pending.queue;
    const isSuccess =
      wsPayload.status === ServiceExecutionStatus.FINISHED &&
      wsPayload.data &&
      Object.keys(wsPayload.data).length > 0;

    const apply = () => {
      if (!queue.cancelled) {
        if (isSuccess) {
          updateStep(pending.participantUuid, pending.stepCode, {
            status: ServiceQueueStepStatus.SUCCESS,
            data: wsPayload.data,
          });
          const participantTypeCode = resolveParticipantTypeCode(
            queue,
            pending.step
          );
          queue.onStepSuccess?.({
            stepCode: pending.stepCode,
            stepName: pending.stepName,
            data: wsPayload.data,
            participantTypeCode,
          });
        } else {
          const err = new Error(
            wsPayload.error_description || "Сервис вернул пустой ответ"
          );
          updateStep(pending.participantUuid, pending.stepCode, {
            status: ServiceQueueStepStatus.ERROR,
            error: err.message,
          });
          const participantTypeCode = resolveParticipantTypeCode(
            queue,
            pending.step
          );
          queue.onStepError?.({
            stepCode: pending.stepCode,
            stepName: pending.stepName,
            error: err,
            participantTypeCode,
          });
        }
      }

      if (!pending.advanced) {
        pending.advanced = true;
        pending.advanceResolve();
      }

      teardownWsSubscriptionIfIdle();
    };

    const elapsed = Date.now() - (pending.stepStartedAt || Date.now());
    const remaining = Math.max(0, MIN_STEP_DURATION_MS - elapsed);
    if (remaining > 0) setTimeout(apply, remaining);
    else apply();
  }

  // Регистрирует ожидание ответа сервиса до запуска, чтобы не пропустить
  // быстрый WS-ответ. rekey() переключает ключ на request_uuid из ответа.
  function registerPending(queue, step, requestUuid, stepStartedAt) {
    let advanceResolve;
    const promise = new Promise((resolve) => {
      advanceResolve = resolve;
    });

    let currentKey = requestUuid;

    const pending = {
      participantUuid: queue.participantUuid,
      stepCode: step.code,
      stepName: step.name,
      step,
      queue,
      advanceResolve,
      advanced: false,
      stepStartedAt: stepStartedAt || Date.now(),
      advanceTimeoutId: null,
      finalTimeoutId: null,
    };

    const hardDeadline = (stepStartedAt || Date.now()) + MAX_STEP_TOTAL_MS;

    pending.advanceTimeoutId = setTimeout(() => {
      if (!pending.advanced) {
        pending.advanced = true;
      }
      advanceResolve();
    }, ADVANCE_AFTER_MS);

    function scheduleFinalTimeout() {
      const delay = Math.max(
        0,
        Math.min(FINAL_TIMEOUT_MS, hardDeadline - Date.now())
      );
      pending.finalTimeoutId = setTimeout(() => {
        if (pendingByRequestUuid.get(currentKey) !== pending) return;
        pendingByRequestUuid.delete(currentKey);

        if (!queue.cancelled) {
          const err = new Error("Таймаут ожидания ответа сервиса");
          updateStep(queue.participantUuid, step.code, {
            status: ServiceQueueStepStatus.ERROR,
            error: err.message,
          });
          queue.onStepError?.({
            stepCode: step.code,
            stepName: step.name,
            error: err,
            participantTypeCode: resolveParticipantTypeCode(queue, step),
          });
        }

        if (!pending.advanced) {
          pending.advanced = true;
          advanceResolve();
        }

        teardownWsSubscriptionIfIdle();
      }, delay);
    }

    scheduleFinalTimeout();

    pending.rescheduleFinalTimeout = () => {
      clearTimeout(pending.finalTimeoutId);
      scheduleFinalTimeout();
    };

    pendingByRequestUuid.set(currentKey, pending);

    function rekey(newKey) {
      if (!newKey || newKey === currentKey) return;
      if (pendingByRequestUuid.get(currentKey) !== pending) return;
      pendingByRequestUuid.delete(currentKey);
      currentKey = newKey;
      pendingByRequestUuid.set(currentKey, pending);
    }

    function cancel() {
      clearTimeout(pending.advanceTimeoutId);
      clearTimeout(pending.finalTimeoutId);
      if (pendingByRequestUuid.get(currentKey) === pending) {
        pendingByRequestUuid.delete(currentKey);
      }
      if (!pending.advanced) {
        pending.advanced = true;
        advanceResolve();
      }
      teardownWsSubscriptionIfIdle();
    }

    return { promise, rekey, cancel };
  }

  function updateStep(participantUuid, stepCode, patch) {
    const queue = queues.get(participantUuid);
    if (!queue) return;
    const step = queue.steps.find((s) => s.code === stepCode);
    if (!step) return;
    Object.assign(step, patch);
    bumpVersion();
  }

  async function processStep(participantUuid, stepIdx) {
    const queue = queues.get(participantUuid);
    if (!queue || queue.cancelled) return;

    const step = queue.steps[stepIdx];
    if (!step) return;

    if (UI_ONLY_STEP_CODES.has(step.code)) {
      updateStep(participantUuid, step.code, {
        status: ServiceQueueStepStatus.SKIPPED,
      });
      return;
    }

    updateStep(participantUuid, step.code, {
      status: ServiceQueueStepStatus.RUNNING,
      error: null,
    });

    const participantTypeCodeForStart = resolveParticipantTypeCode(queue, step);
    queue.onStepStart?.({
      stepCode: step.code,
      stepName: step.name,
      participantTypeCode: participantTypeCodeForStart,
    });

    const stepStartedAt = Date.now();
    const clientRequestUuid = generateRequestUuid();

    // Подписываемся и регистрируем ожидание ДО запуска сервиса — иначе быстрый
    // WS-ответ может прийти раньше, чем мы начнём его слушать (гонка).
    ensureWsSubscription();
    const waiter = registerPending(
      queue,
      step,
      clientRequestUuid,
      stepStartedAt
    );

    try {
      const schemaResponse = await getServiceListWithResponseByCode(step.code);
      if (queue.cancelled) return;

      if (!schemaResponse?.success || !schemaResponse.payload) {
        throw new Error("Не удалось получить схему сервиса");
      }

      const schema = schemaResponse.payload;
      const payload = buildPayload(schema, queue.identifier, {
        personIdentifier: queue.personIdentifier,
        businessIdentifier: queue.businessIdentifier,
      });
      payload.request_uuid = clientRequestUuid;

      const runResponse = await runChainServiceByIndividual(
        queue.executionUuid,
        step.uuid,
        queue.txId,
        payload
      );
      if (queue.cancelled) return;

      const chainRunData =
        runResponse?.payload?.chain_run?.payload || runResponse?.payload;

      if (!runResponse?.success || !chainRunData?.run) {
        throw new Error("Сервис не запустился");
      }

      waiter.rekey(chainRunData.request_uuid);
    } catch (e) {
      if (queue.cancelled) return;
      waiter.cancel();
      const elapsed = Date.now() - stepStartedAt;
      const remaining = Math.max(0, MIN_STEP_DURATION_MS - elapsed);
      if (remaining > 0) await sleep(remaining);
      if (queue.cancelled) return;
      updateStep(participantUuid, step.code, {
        status: ServiceQueueStepStatus.ERROR,
        error: e?.message || String(e),
      });
      queue.onStepError?.({
        stepCode: step.code,
        stepName: step.name,
        error: e,
        participantTypeCode: resolveParticipantTypeCode(queue, step),
      });
      return;
    }

    if (queue.cancelled) return;

    await waiter.promise;
  }

  function resolveParticipantTypeCode(queue, step) {
    return queue.participantTypeCode || step.participant_type_code || null;
  }

  function hasPendingForParticipant(participantUuid) {
    for (const pending of pendingByRequestUuid.values()) {
      if (pending.participantUuid === participantUuid) return true;
    }
    return false;
  }

  async function runQueue(participantUuid) {
    const queue = queues.get(participantUuid);
    if (!queue) return;

    const totalSteps = queue.steps.filter(
      (s) => !UI_ONLY_STEP_CODES.has(s.code)
    ).length;
    queue.onQueueStart?.({ participantUuid, totalSteps });

    for (let i = 0; i < queue.steps.length; i++) {
      if (queue.cancelled) break;
      queue.currentIdx = i;
      bumpVersion();
      await processStep(participantUuid, i);
    }

    while (!queue.cancelled && hasPendingForParticipant(participantUuid)) {
      await sleep(500);
    }

    queue.finished = true;
    bumpVersion();

    if (!queue.cancelled) {
      const summary = queue.steps.reduce(
        (acc, s) => {
          if (s.status === ServiceQueueStepStatus.SUCCESS) acc.success++;
          else if (s.status === ServiceQueueStepStatus.ERROR) acc.error++;
          return acc;
        },
        { success: 0, error: 0 }
      );
      queue.onQueueFinish?.({ participantUuid, summary, totalSteps });
    }
  }

  const lastStartParams = new Map();

  function startQueue(params) {
    const {
      participantUuid,
      participantType,
      participantTypeCode,
      participantLabel,
      executionUuid,
      txId,
      identifier,
      personIdentifier,
      businessIdentifier,
      steps,
      onStepStart,
      onStepSuccess,
      onStepError,
      onQueueStart,
      onQueueFinish,
      onQueueCancel,
    } = params;

    if (!participantUuid || !executionUuid || !txId) return;
    if (!Array.isArray(steps) || steps.length === 0) return;

    cancelQueue(participantUuid);

    lastStartParams.set(participantUuid, params);

    const queue = {
      participantUuid,
      participantType,
      participantTypeCode,
      participantLabel: participantLabel || "",
      executionUuid,
      txId,
      identifier,
      personIdentifier,
      businessIdentifier,
      steps: steps.map(createStepState),
      currentIdx: -1,
      cancelled: false,
      finished: false,
      onStepStart,
      onStepSuccess,
      onStepError,
      onQueueStart,
      onQueueFinish,
      onQueueCancel,
    };
    queues.set(participantUuid, queue);
    bumpVersion();

    runQueue(participantUuid);
  }

  function rerunQueue(participantUuid) {
    const params = lastStartParams.get(participantUuid);
    if (!params) return;
    startQueue(params);
  }

  function cancelQueue(participantUuid) {
    const queue = queues.get(participantUuid);
    if (!queue) return;
    queue.cancelled = true;

    for (const [requestUuid, pending] of pendingByRequestUuid.entries()) {
      if (pending.participantUuid === participantUuid) {
        clearTimeout(pending.advanceTimeoutId);
        clearTimeout(pending.finalTimeoutId);
        pendingByRequestUuid.delete(requestUuid);
        if (!pending.advanced) {
          pending.advanced = true;
          pending.advanceResolve();
        }
      }
    }

    queue.onQueueCancel?.({ participantUuid });

    queues.delete(participantUuid);
    teardownWsSubscriptionIfIdle();
    bumpVersion();
  }

  function cancelAll() {
    for (const participantUuid of queues.keys()) {
      cancelQueue(participantUuid);
    }
    queues.clear();
    pendingByRequestUuid.clear();
    lastStartParams.clear();
    if (wsUnsubscribe.value) {
      wsUnsubscribe.value();
      wsUnsubscribe.value = null;
    }
    bumpVersion();
  }

  function getStepStatus(participantUuid, stepCode) {
    void version.value;
    const queue = queues.get(participantUuid);
    if (!queue) return null;
    const step = queue.steps.find((s) => s.code === stepCode);
    return step?.status || null;
  }

  function getStepData(participantUuid, stepCode) {
    void version.value;
    const queue = queues.get(participantUuid);
    if (!queue) return null;
    const step = queue.steps.find((s) => s.code === stepCode);
    return step?.data || null;
  }

  function getProgress(participantUuid) {
    void version.value;
    const queue = queues.get(participantUuid);
    if (!queue) return null;
    const total = queue.steps.filter(
      (s) => !UI_ONLY_STEP_CODES.has(s.code)
    ).length;
    const done = queue.steps.filter(
      (s) =>
        s.status === ServiceQueueStepStatus.SUCCESS ||
        s.status === ServiceQueueStepStatus.ERROR
    ).length;
    return { done, total, running: !queue.finished && !queue.cancelled };
  }

  function buildQueueSummary(queue) {
    const visibleSteps = queue.steps.filter(
      (s) => !UI_ONLY_STEP_CODES.has(s.code)
    );
    const total = visibleSteps.length;
    const done = visibleSteps.filter(
      (s) =>
        s.status === ServiceQueueStepStatus.SUCCESS ||
        s.status === ServiceQueueStepStatus.ERROR
    ).length;
    const currentStep =
      queue.steps.find((s) => s.status === ServiceQueueStepStatus.RUNNING) ||
      null;
    return {
      participantUuid: queue.participantUuid,
      participantLabel: queue.participantLabel || "",
      steps: visibleSteps,
      currentStep,
      finished: queue.finished,
      cancelled: queue.cancelled,
      running: !queue.finished && !queue.cancelled,
      done,
      total,
    };
  }

  function getQueueForParticipant(participantUuid) {
    void version.value;
    const queue = queues.get(participantUuid);
    if (!queue) return null;
    return buildQueueSummary(queue);
  }

  function getAllQueues() {
    void version.value;
    const result = [];
    for (const queue of queues.values()) {
      if (queue.cancelled) continue;
      const summary = buildQueueSummary(queue);
      if (summary.total === 0) continue;
      result.push(summary);
    }
    return result.reverse();
  }

  function hasActiveQueues() {
    void version.value;
    for (const queue of queues.values()) {
      if (!queue.cancelled && !queue.finished) return true;
    }
    return false;
  }

  function hasAnyQueues() {
    void version.value;
    for (const queue of queues.values()) {
      if (!queue.cancelled) return true;
    }
    return false;
  }

  function getOverallProgress() {
    void version.value;
    let done = 0;
    let total = 0;
    let running = false;
    const list = [];

    for (const queue of queues.values()) {
      if (queue.cancelled) continue;

      const queueTotal = queue.steps.filter(
        (s) => !UI_ONLY_STEP_CODES.has(s.code)
      ).length;
      const queueDone = queue.steps.filter(
        (s) =>
          s.status === ServiceQueueStepStatus.SUCCESS ||
          s.status === ServiceQueueStepStatus.ERROR
      ).length;
      const isRunning = !queue.finished;

      done += queueDone;
      total += queueTotal;
      if (isRunning) running = true;

      const currentStep = queue.steps.find(
        (s) => s.status === ServiceQueueStepStatus.RUNNING
      );

      list.push({
        participantUuid: queue.participantUuid,
        participantLabel: queue.participantLabel || "",
        done: queueDone,
        total: queueTotal,
        running: isRunning,
        currentStepName: currentStep?.name || null,
      });
    }

    return { done, total, running, queues: list };
  }

  return {
    startQueue,
    rerunQueue,
    cancelQueue,
    cancelAll,
    getStepStatus,
    getStepData,
    getProgress,
    getQueueForParticipant,
    getAllQueues,
    hasActiveQueues,
    hasAnyQueues,
    getOverallProgress,
  };
});
